/**
 * Operator-declared evacuation-order detail panel (#445-(3); correct + coordinate #459/#458).
 *
 * Shows the level/status + the issuing authority (type + named body) and the escalate
 * (ALERT→ORDER) / rescind lifecycle actions. Reuses the violet evacuation palette
 * (evacuationStyle) — the same protective-action register as the external GeoBC layer —
 * but is unmistakably attributed as RECORDED IN-COP on behalf of the named authority
 * (the COP coordinates the order; it does NOT confer the legal power to evacuate). The
 * recorder is ORTHOGONAL to the issuer.
 *
 * #458 — a coordinating authority (the Far-North tripartite case: a Band ISSUES, the PEOC
 * COORDINATES) renders as a SECONDARY "Coordinated by <name>" block AFTER "Issued by",
 * never above it (§4.2 coordination ≠ command; FN-dignity).
 * #459 — the order is CORRECTABLE: "Correct" opens an inline edit form (name / population /
 * notes / issuing + coordinating attribution → "Update the record") + a "Correct the area
 * on the map" affordance that redraws the polygon. A correction is NOT a rescind — the
 * escalate/rescind lifecycle is intact. Editing auto-enables the layer so the operator
 * sees the polygon it is correcting.
 */
import { useEffect, useState } from 'react';
import type {
  IncidentHazardType,
  IssuingAuthorityType,
  OperatorEvacOrderFeature,
  EvacuationSurface,
  PublicAlertAuthorityKind,
} from '@tsr/shared-types';
import {
  ISSUING_AUTHORITY_TYPES,
  ISSUING_AUTHORITY_TYPE_LABELS,
  PUBLIC_ALERT_REQUEST_FROM_EVAC_NOTE,
  isFirstNationEvacuation,
  FN_EVAC_REQUEST_BADGE,
  FN_EVAC_REQUEST_RECIPIENT,
  FN_EVAC_DECISION_LABEL,
  FN_EVAC_PUBLIC_ALERT_NOTE,
} from '@tsr/shared-types';
import { PublicAlertRequestBlock } from './components/PublicAlertRequestBlock';
import {
  escalateEvacuationOrder,
  rescindEvacuationOrder,
  patchEvacuationOrderMetadata,
  startEvacuationAccounting,
  fetchEvacuationSurface,
} from './api';
import {
  CANCEL_EVAC_CORRECTION_LABEL,
  COORDINATED_BY_LABEL,
  COORDINATING_AUTHORITY_OPTIONAL_LABEL,
  COORDINATING_BODY_NAMED_LABEL,
  CORRECT_EVAC_AREA_LABEL,
  CORRECT_EVAC_ORDER_LABEL,
  UPDATE_EVAC_RECORD_BUSY_LABEL,
  UPDATE_EVAC_RECORD_LABEL,
  EVAC_ACCOUNTING_HEADING,
  EVAC_ACCOUNTING_AWAITING_AIRLIFT_SHORT,
  EVAC_ACCOUNTING_UNACCOUNTED_SHORT,
  EVAC_ACCOUNTING_PENDING_LABEL,
  EVAC_ACCOUNTING_INCOMPLETE_PREFIX,
  START_EVAC_ACCOUNTING_LABEL,
  START_EVAC_ACCOUNTING_BUSY_LABEL,
  RECORD_EVAC_ACCOUNTING_LABEL,
} from './incidentVocabulary';
import { evacuationStyle } from './styles/evacuationStyles';
import { getSessionId } from './lib/operatorActions';
import { showToast } from './lib/toast';
import { LinkToIncidentControl } from './LinkToIncidentControl';
import { EvacProgressBar, awaitingAirliftDisplay } from './EvacuationBody';
import { HeadcountEditorForm } from './HeadcountEditorForm';

function whenLabel(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

function num(n: number | null): string {
  return n === null ? '—' : n.toLocaleString();
}

/** Hazards the "start accounting" originate-path can stamp on a NEW incident (used ONLY
 *  when there is no proximate open incident to attach to). Defaults to 'other' so the
 *  operator need not pick — the attach path ignores it entirely. */
const HAZARD_OPTIONS: { value: IncidentHazardType; label: string }[] = [
  { value: 'other', label: 'Other' },
  { value: 'flood', label: 'Flood' },
  { value: 'wildfire', label: 'Wildfire' },
  { value: 'ice_storm', label: 'Ice storm' },
];

/**
 * #462 — the EVACUATION ACCOUNTING section surfaced FROM the order. The founder-directive
 * crux: the Municipal EOC / First Nation / PEOC seat runs the post-order accounting
 * (evacuated / remaining / awaiting a flight / not-yet-accounted-for) IN-COP instead of
 * breaking to paper.
 *
 * State machine, keyed off the order's PERSISTED incident link (incident_link_mode ===
 * 'manual' — the read-time 'auto' projection is NOT an accounting anchor; the order isn't
 * attached yet):
 *   - NOT linked → "Start evacuee accounting": attaches the order to a proximate open
 *     incident, else originates one from the order (hazard picker, default 'other').
 *   - LINKED → the live §4.30 rollup (aggregate + per-community) with the UNKNOWN≠ZERO
 *     honesty (pending communities read "Not yet counted", never a zero all-clear), and a
 *     per-community "Record" affordance (headcount incl. awaiting-flight).
 */
function EvacOrderAccountingSection({
  order,
  onOrderLinked,
}: {
  order: OperatorEvacOrderFeature;
  onOrderLinked?: (updated: OperatorEvacOrderFeature) => void;
}): JSX.Element {
  const p = order.properties;
  const isFN = isFirstNationEvacuation(p.issuing_authority_type);
  const persistedIncidentId = p.incident_link_mode === 'manual' ? p.incident_id : null;
  const [startedIncidentId, setStartedIncidentId] = useState<string | null>(null);
  const [hazard, setHazard] = useState<IncidentHazardType>('other');
  const [busy, setBusy] = useState(false);
  const [surface, setSurface] = useState<EvacuationSurface | null>(null);
  const [error, setError] = useState(false);
  const [recordFor, setRecordFor] = useState<{ id: string; name: string } | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  // A fresh order in the panel resets the just-started link (props are the source of truth).
  useEffect(() => {
    setStartedIncidentId(null);
  }, [order.id]);

  const incidentId = persistedIncidentId ?? startedIncidentId;

  useEffect(() => {
    if (!incidentId) {
      setSurface(null);
      return;
    }
    const ctl = new AbortController();
    setSurface(null);
    setError(false);
    void fetchEvacuationSurface(incidentId, ctl.signal)
      .then((s) => setSurface(s))
      .catch(() => {
        if (!ctl.signal.aborted) setError(true);
      });
    return () => ctl.abort();
  }, [incidentId, reloadTick]);

  const start = (): void => {
    if (busy) return;
    setBusy(true);
    void startEvacuationAccounting(order.id, { session_id: getSessionId(), hazard_type: hazard })
      .then((res) => {
        setStartedIncidentId(res.accounting_incident_id);
        if (res.order) onOrderLinked?.(res.order as OperatorEvacOrderFeature);
        const msg =
          res.mode === 'created'
            ? `Evacuee accounting started — incident ${res.incident_number ?? ''} created.`
            : res.mode === 'attached'
              ? 'Evacuee accounting started — attached to the responding incident.'
              : 'Evacuee accounting already underway.';
        showToast(msg, { variant: 'success' });
      })
      .catch((err) =>
        showToast((err as Error).message || 'Could not start accounting. Please retry.', {
          variant: 'error',
        }),
      )
      .finally(() => setBusy(false));
  };

  return (
    <section
      className="rounded-lg border border-slate-700 bg-slate-900/60 px-3 py-3 space-y-2"
      data-testid="evac-order-accounting"
    >
      <h3 className="text-xs uppercase tracking-wider text-slate-400">{EVAC_ACCOUNTING_HEADING}</h3>

      {!incidentId ? (
        <div className="space-y-2" data-testid="evac-accounting-start">
          <p className="text-[11px] text-slate-400 leading-relaxed">
            Begin accounting for the residents this {isFN ? 'request' : 'order'} covers — evacuated,
            remaining in community, awaiting a flight, and not yet accounted for.
          </p>
          <label className="block text-[11px] text-slate-500 space-y-1">
            <span>Hazard (only if a new incident is created)</span>
            <select
              data-testid="evac-accounting-hazard"
              value={hazard}
              onChange={(e) => setHazard(e.target.value as IncidentHazardType)}
              className="w-full text-xs bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-slate-100"
            >
              {HAZARD_OPTIONS.map((h) => (
                <option key={h.value} value={h.value}>
                  {h.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            data-testid="evac-accounting-start-btn"
            disabled={busy}
            onClick={start}
            className="w-full text-xs py-1.5 px-3 rounded bg-rose-600/90 hover:bg-rose-600 border border-rose-500 text-white font-medium disabled:opacity-50 disabled:cursor-wait"
          >
            {busy ? START_EVAC_ACCOUNTING_BUSY_LABEL : START_EVAC_ACCOUNTING_LABEL}
          </button>
        </div>
      ) : error ? (
        <p className="text-[11px] text-slate-400" data-testid="evac-accounting-error">
          Could not load the accounting for this incident.
        </p>
      ) : !surface ? (
        <p className="text-[11px] text-slate-500" data-testid="evac-accounting-loading">
          Assembling evacuee accounting…
        </p>
      ) : (
        <div className="space-y-2" data-testid="evac-accounting-rollup">
          <EvacProgressBar
            evacuated={surface.aggregate.total_evacuated}
            remaining={surface.aggregate.total_remaining}
            pendingCount={surface.aggregate.pending_community_count}
          />
          <div className="grid grid-cols-2 gap-1.5">
            <div className="rounded bg-indigo-500/10 border border-indigo-500/30 px-2 py-1 flex items-baseline justify-between">
              <span className="text-[10px] uppercase tracking-wide text-indigo-200/80">
                {EVAC_ACCOUNTING_AWAITING_AIRLIFT_SHORT}
              </span>
              <span className="font-mono text-sm text-indigo-200" data-testid="evac-accounting-awaiting">
                {awaitingAirliftDisplay(
                  surface.aggregate.total_awaiting_airlift,
                  surface.aggregate.awaiting_airlift_unknown_count,
                )}
              </span>
            </div>
            <div className="rounded bg-amber-500/10 border border-amber-500/25 px-2 py-1 flex items-baseline justify-between">
              <span className="text-[10px] uppercase tracking-wide text-amber-200/80">
                {EVAC_ACCOUNTING_UNACCOUNTED_SHORT}
              </span>
              <span className="font-mono text-sm text-amber-200">
                {surface.aggregate.total_unreachable.toLocaleString()}
              </span>
            </div>
          </div>
          {surface.aggregate.pending_community_count > 0 && (
            <p className="text-[10px] text-amber-300" data-testid="evac-accounting-pending-note">
              {surface.aggregate.pending_community_count} of {surface.aggregate.community_count}{' '}
              {EVAC_ACCOUNTING_INCOMPLETE_PREFIX} — no headcount recorded (not zero).
            </p>
          )}

          <ul className="space-y-1" data-testid="evac-accounting-communities">
            {surface.communities.map((c) => (
              <li
                key={c.id}
                className="flex items-center gap-2 rounded border border-slate-700/70 bg-slate-800/50 px-2 py-1"
              >
                <span className="min-w-0 flex-1">
                  <span className="block text-xs truncate">{c.name}</span>
                  {c.accounting_state === 'pending' ? (
                    <span className="block text-[9px] text-amber-300" data-testid="evac-accounting-community-pending">
                      {EVAC_ACCOUNTING_PENDING_LABEL}
                    </span>
                  ) : (
                    <span className="block text-[9px] text-slate-500">
                      {num(c.evacuated)} evac · {num(c.remaining)} remaining
                      {c.awaiting_airlift != null
                        ? ` · ${c.awaiting_airlift.toLocaleString()} awaiting flight`
                        : ''}
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  data-testid="evac-accounting-record"
                  onClick={() => setRecordFor({ id: c.id, name: c.name })}
                  className="text-[10px] py-1 px-2 rounded bg-slate-700 hover:bg-slate-600 border border-slate-600 text-slate-200 flex-shrink-0"
                >
                  {RECORD_EVAC_ACCOUNTING_LABEL}
                </button>
              </li>
            ))}
          </ul>
          <p className="text-[10px] text-slate-500 leading-relaxed">
            The counts are each community’s own. Recording accounting never claims to track
            residents — it records what the community reports.
          </p>
        </div>
      )}

      {recordFor && incidentId && (
        <HeadcountEditorForm
          community={recordFor}
          incidentId={incidentId}
          defaultSource="community_official"
          onClose={() => setRecordFor(null)}
          onSubmitted={() => {
            setRecordFor(null);
            setReloadTick((t) => t + 1);
          }}
        />
      )}
    </section>
  );
}

export function OperatorEvacOrderBody({
  feature,
  onChanged,
  onCorrectGeometry,
  onEnsureVisible,
}: {
  feature: OperatorEvacOrderFeature;
  /** Fired after a successful escalate/rescind/correct so the layer + panel can refresh. */
  onChanged?: (updated: OperatorEvacOrderFeature) => void;
  /** #459 — start the on-map polygon redraw (the App owns the draw tool + globe). */
  onCorrectGeometry?: () => void;
  /** #459 — auto-enable the OperatorEvacuationOrderLayer so the operator sees the polygon
   *  it is correcting (reuses #456's post-declare auto-enable). Called when editing opens. */
  onEnsureVisible?: () => void;
}): JSX.Element {
  const p = feature.properties;
  const style = evacuationStyle(p.level);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);

  // Correction (metadata) edit fields — seeded from the order when the form opens.
  const [eName, setEName] = useState('');
  const [ePop, setEPop] = useState('');
  const [eNotes, setENotes] = useState('');
  const [eIssType, setEIssType] = useState<IssuingAuthorityType>('municipal');
  const [eIssName, setEIssName] = useState('');
  const [eCoordType, setECoordType] = useState<IssuingAuthorityType>('provincial');
  const [eCoordName, setECoordName] = useState('');

  const authorityLabel = ISSUING_AUTHORITY_TYPE_LABELS[p.issuing_authority_type];
  const isActive = p.status === 'active';
  // #637 — a First Nation record is a REQUEST to the PEOC, not an order: no issuer, no
  // alert/order register, no escalation (there is no evacuation order on a reserve to
  // escalate to — PERP §6.6.3.1/§6.6.3.3). The panel reframes off the issuing authority type.
  const isFN = isFirstNationEvacuation(p.issuing_authority_type);
  const canEscalate = isActive && p.level === 'alert' && !isFN;

  // #461 — pre-seed the public-alert-request authority KIND from the order's issuing
  // authority. The #497 request supports municipal | first_nation; a first_nation issuer
  // maps to first_nation, everything else (municipal/provincial/federal/other) to municipal.
  // The operator can still toggle it in the request form.
  const requestAuthorityKind: PublicAlertAuthorityKind =
    p.issuing_authority_type === 'first_nation' ? 'first_nation' : 'municipal';

  const escalate = (): void => {
    if (busy) return;
    setBusy(true);
    void escalateEvacuationOrder(feature.id, { session_id: getSessionId() })
      .then((updated) => {
        // #637 — unreachable for a First Nation (the escalate control is hidden + the route
        // 422s), but keep the copy off "issued by" defensively.
        showToast(`Escalated to ORDER — issued by ${p.issuing_authority_name}.`, {
          variant: 'success',
        });
        onChanged?.(updated);
      })
      .catch((err) => showToast((err as Error).message || 'Could not escalate.', { variant: 'error' }))
      .finally(() => setBusy(false));
  };

  const rescind = (): void => {
    if (busy) return;
    setBusy(true);
    void rescindEvacuationOrder(feature.id, { session_id: getSessionId() })
      .then((updated) => {
        showToast(isFN ? 'Evacuation request rescinded.' : `Evacuation ${p.level} rescinded.`, {
          variant: 'success',
        });
        onChanged?.(updated);
      })
      .catch((err) => showToast((err as Error).message || 'Could not rescind.', { variant: 'error' }))
      .finally(() => setBusy(false));
  };

  // #459 — open the inline correction form. Seed from the current record + auto-enable the
  // layer so the operator sees the polygon it is correcting.
  const startCorrection = (): void => {
    setEName(p.name ?? '');
    setEPop(p.population_estimate === null ? '' : String(p.population_estimate));
    setENotes(p.notes ?? '');
    setEIssType(p.issuing_authority_type);
    setEIssName(p.issuing_authority_name);
    setECoordType(p.coordinating_authority_type ?? 'provincial');
    setECoordName(p.coordinating_authority_name ?? '');
    setEditing(true);
    onEnsureVisible?.();
  };

  const submitCorrection = (): void => {
    if (busy) return;
    if (eIssName.trim().length === 0) {
      showToast('Name the issuing authority — the COP records the order on its behalf.', {
        variant: 'error',
      });
      return;
    }
    const popText = ePop.trim();
    let population: number | null = null;
    if (popText !== '') {
      const n = Number(popText);
      if (!Number.isInteger(n) || n < 0) {
        showToast('Population estimate must be a non-negative whole number.', { variant: 'error' });
        return;
      }
      population = n;
    }
    const coordName = eCoordName.trim();
    setBusy(true);
    void patchEvacuationOrderMetadata(feature.id, {
      session_id: getSessionId(),
      name: eName.trim() || null,
      population_estimate: population,
      notes: eNotes.trim() || null,
      issuing_authority_type: eIssType,
      issuing_authority_name: eIssName.trim(),
      // Empty coordinating name CLEARS the coordinator (both fields null together).
      coordinating_authority_type: coordName ? eCoordType : null,
      coordinating_authority_name: coordName || null,
    })
      .then((updated) => {
        showToast('Evacuation record updated.', { variant: 'success' });
        setEditing(false);
        onChanged?.(updated);
      })
      .catch((err) =>
        showToast((err as Error).message || 'Could not update the record. Please retry.', {
          variant: 'error',
        }),
      )
      .finally(() => setBusy(false));
  };

  const inputCls =
    'w-full text-xs bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-slate-100 placeholder-slate-500';

  return (
    <div className="px-4 py-4 space-y-4" data-testid="operator-evac-order-body">
      <div className="border-l-2 pl-3" style={{ borderColor: style.color }}>
        <p className="text-[10px] uppercase tracking-wider text-slate-500">
          {isFN ? 'Evacuation request · recorded in-COP' : `Evacuation ${p.level === 'order' ? 'order' : 'alert'} · recorded in-COP`}
        </p>
        <h2 className="text-base font-semibold">{p.name || 'Unnamed evacuation area'}</h2>
        <span
          className="inline-flex items-center gap-1.5 mt-1.5 text-[11px] px-2 py-0.5 rounded font-medium"
          style={{ backgroundColor: `${style.color}22`, color: style.color }}
        >
          <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: style.color }} />
          {isFN ? FN_EVAC_REQUEST_BADGE : style.label}
          {p.status === 'rescinded' && <span className="text-slate-400">· rescinded</span>}
        </span>
      </div>

      {/* #637 — a First Nation evacuation is a REQUEST to the PEOC: NO issuer (no reserve
          "issued by") — it is submitted to the PEOC (ISC included), with the affected First
          Nation named as CONTEXT, never as an issuer. Municipal/provincial keep the PRIMARY
          "Issued by" attribution (records-not-confers). */}
      {isFN ? (
        <section
          className="rounded-lg border border-slate-700 bg-slate-900/60 px-3 py-2"
          data-testid="evac-fn-request"
        >
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Submitted to</p>
          <p className="text-sm font-semibold" data-testid="evac-fn-request-recipient">
            {FN_EVAC_REQUEST_RECIPIENT}
          </p>
          <p className="text-[11px] text-slate-400" data-testid="evac-fn-community">
            First Nation community: {p.issuing_authority_name}
          </p>
        </section>
      ) : (
        <section className="rounded-lg border border-slate-700 bg-slate-900/60 px-3 py-2">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Issued by</p>
          <p className="text-sm font-semibold" data-testid="evac-issuing-authority">
            {p.issuing_authority_name}
          </p>
          <p className="text-[11px] text-slate-400">{authorityLabel}</p>
        </section>
      )}

      {/* #637 — the OPTIONAL evacuation-decision provenance (the Joint Command Call). Rendered
          from the free-text field on the First Nation path, under a NON-issuer label. */}
      {isFN && p.notes && (
        <section
          className="rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-1.5"
          data-testid="evac-fn-decision"
        >
          <p className="text-[10px] uppercase tracking-wider text-slate-500">{FN_EVAC_DECISION_LABEL}</p>
          <p className="text-xs text-slate-300 whitespace-pre-wrap">{p.notes}</p>
        </section>
      )}

      {/* #458 — the coordinating authority (Far-North tripartite case). SECONDARY: dimmer
          border, smaller text, no bold — and rendered AFTER "Issued by", never above it
          (§4.2 coordination ≠ command; FN-dignity). Absent when there is no coordinator. */}
      {p.coordinating_authority_name && (
        <section
          className="rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-1.5"
          data-testid="evac-coordinating-authority-section"
        >
          <p className="text-[10px] uppercase tracking-wider text-slate-500">{COORDINATED_BY_LABEL}</p>
          <p className="text-xs text-slate-300" data-testid="evac-coordinating-authority">
            {p.coordinating_authority_name}
          </p>
        </section>
      )}

      {/* R2/#478 — this evacuation is a PROTECTIVE ACTION; link it to the incident it
          protects (or leave it standalone). Auto-links by proximity; always unlinkable. */}
      <LinkToIncidentControl
        kind="operator_evac_order"
        alertId={feature.id}
        incidentId={p.incident_id}
        linkMode={p.incident_link_mode ?? null}
      />

      <section>
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Details</h3>
        <dl className="grid grid-cols-[8rem_1fr] gap-y-1 text-sm">
          <dt className="text-slate-400">Status</dt>
          <dd className="capitalize">{p.status}</dd>
          <dt className="text-slate-400">Affected (est.)</dt>
          <dd>{num(p.population_estimate)}</dd>
          <dt className="text-slate-400">{isFN ? 'Requested' : 'Ordered'}</dt>
          <dd className="text-xs">{whenLabel(p.declared_at)}</dd>
          {p.escalated_at && (
            <>
              <dt className="text-slate-400">Escalated</dt>
              <dd className="text-xs">{whenLabel(p.escalated_at)}</dd>
            </>
          )}
          {p.rescinded_at && (
            <>
              <dt className="text-slate-400">Rescinded</dt>
              <dd className="text-xs">{whenLabel(p.rescinded_at)}</dd>
            </>
          )}
          <dt className="text-slate-400">Recorded by</dt>
          <dd className="font-mono text-xs">{p.created_by_operator_id || '—'}</dd>
        </dl>
        {/* #637 — on the First Nation path the free-text is the evacuation-decision provenance,
            already rendered above under its own label — don't repeat it here. */}
        {!isFN && p.notes && (
          <p className="mt-2 text-xs text-slate-300 whitespace-pre-wrap">{p.notes}</p>
        )}
      </section>

      {/* #462 — EVACUATION ACCOUNTING surfaced FROM the order. Shown for ORDER-level (the
          mandatory-leave register accounting is meaningful for) and for any order already
          linked (after-action stays retrievable). An ALERT (be-ready) hides it until it
          escalates. Not shown mid-correction so the edit form has focus. */}
      {(p.level === 'order' || isFN || p.incident_link_mode === 'manual') && !editing && (
        <EvacOrderAccountingSection order={feature} onOrderLinked={onChanged} />
      )}

      {/* #459 — the inline CORRECTION form (metadata). A correction is NOT a rescind. */}
      {editing && (
        <section
          className="rounded-lg border border-violet-700/50 bg-slate-900/70 px-3 py-3 space-y-2.5"
          data-testid="evac-edit-form"
        >
          <label className="block text-[11px] text-slate-400 space-y-1">
            <span>Area name</span>
            <input
              data-testid="evac-edit-name"
              value={eName}
              onChange={(e) => setEName(e.target.value)}
              placeholder="e.g. Kashechewan riverfront"
              className={inputCls}
            />
          </label>
          <label className="block text-[11px] text-slate-400 space-y-1">
            <span>Affected population (estimate)</span>
            <input
              type="number"
              min={0}
              step={1}
              data-testid="evac-edit-population"
              value={ePop}
              onChange={(e) => setEPop(e.target.value)}
              placeholder="—"
              className={inputCls}
            />
          </label>
          {/* #637 — a First Nation record has NO issuer and NO coordinating authority to
              correct (the request stands as submitted to the PEOC, ISC included); these
              fields are hidden on that path. Municipal/provincial keep the full correction. */}
          {!isFN && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <label className="block text-[11px] text-slate-400 space-y-1">
                  <span>Issuing authority type *</span>
                  <select
                    data-testid="evac-edit-issuing-type"
                    value={eIssType}
                    onChange={(e) => setEIssType(e.target.value as IssuingAuthorityType)}
                    className={inputCls}
                  >
                    {ISSUING_AUTHORITY_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {ISSUING_AUTHORITY_TYPE_LABELS[t]}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-[11px] text-slate-400 space-y-1">
                  <span>Issuing body (named) *</span>
                  <input
                    data-testid="evac-edit-issuing-name"
                    value={eIssName}
                    onChange={(e) => setEIssName(e.target.value)}
                    placeholder="e.g. Township of Elliot Lake"
                    className={inputCls}
                  />
                </label>
              </div>
              {/* Coordinator — SECONDARY. Labelled "Coordinated by (optional)"; clearing the
                  name clears the coordinator. Never a "coordinating authority" noun. */}
              <div className="grid grid-cols-2 gap-2">
                <label className="block text-[11px] text-slate-400 space-y-1">
                  <span>{COORDINATING_AUTHORITY_OPTIONAL_LABEL}</span>
                  <select
                    data-testid="evac-edit-coordinating-type"
                    value={eCoordType}
                    onChange={(e) => setECoordType(e.target.value as IssuingAuthorityType)}
                    className={inputCls}
                  >
                    {ISSUING_AUTHORITY_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {ISSUING_AUTHORITY_TYPE_LABELS[t]}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-[11px] text-slate-400 space-y-1">
                  <span>{COORDINATING_BODY_NAMED_LABEL}</span>
                  <input
                    data-testid="evac-edit-coordinating-name"
                    value={eCoordName}
                    onChange={(e) => setECoordName(e.target.value)}
                    placeholder="e.g. Provincial Emergency Operations Centre"
                    className={inputCls}
                  />
                </label>
              </div>
            </>
          )}
          <label className="block text-[11px] text-slate-400 space-y-1">
            <span>{isFN ? 'Evacuation decision & context (optional)' : 'Notes'}</span>
            <textarea
              data-testid="evac-edit-notes"
              value={eNotes}
              onChange={(e) => setENotes(e.target.value)}
              rows={2}
              placeholder={
                isFN
                  ? 'e.g. Joint Command Call — Chief and Council with ISC; routes, receiving site'
                  : 'Optional context (routes, receiving site, …)'
              }
              className={inputCls}
            />
          </label>
          <div className="flex flex-wrap items-center gap-2 pt-0.5">
            <button
              type="button"
              data-testid="evac-metadata-submit"
              disabled={busy}
              onClick={submitCorrection}
              className="text-xs py-1.5 px-3 rounded bg-violet-700/90 hover:bg-violet-700 border border-violet-500 text-white font-medium disabled:opacity-50 disabled:cursor-wait"
            >
              {busy ? UPDATE_EVAC_RECORD_BUSY_LABEL : UPDATE_EVAC_RECORD_LABEL}
            </button>
            {onCorrectGeometry && (
              <button
                type="button"
                data-testid="evac-correct-area"
                disabled={busy}
                onClick={onCorrectGeometry}
                className="text-xs py-1.5 px-3 rounded bg-slate-800 hover:bg-slate-700 border border-slate-600 text-slate-200 disabled:opacity-50"
              >
                {CORRECT_EVAC_AREA_LABEL}
              </button>
            )}
            <button
              type="button"
              data-testid="evac-edit-cancel"
              onClick={() => setEditing(false)}
              className="ml-auto text-xs py-1.5 px-3 rounded bg-slate-800 hover:bg-slate-700 border border-slate-600 text-slate-300"
            >
              {CANCEL_EVAC_CORRECTION_LABEL}
            </button>
          </div>
        </section>
      )}

      {/* #461 — RAISE a public-alert REQUEST from this evacuation order (the evac-order →
          public-alert bridge). A DISTINCT, deliberately-triggered SECOND step: it is NOT
          part of declare/escalate/rescind — the operator opens the request form and submits
          it here (two verbs, two clicks, two audit rows). It REUSES the #497 rails entirely:
          the request INHERITS this order's issuing authority (pre-filled) + area and routes
          TO the PEOC (the only issuer — PERP §6; records-not-confers), LINKED back to the
          order. Shown only for an ACTIVE order, and hidden mid-correction so the edit form
          keeps focus. */}
      {isActive && !editing && (
        <section
          className="rounded-lg border border-slate-700 bg-slate-900/60 px-3 py-3 space-y-2"
          data-testid="evac-order-public-alert"
        >
          <p className="text-[11px] text-slate-400 leading-relaxed">
            {/* #637 — the shared note names "this order's issuing authority", which does not
                exist on a reserve; the FN variant names the First Nation and keeps "order" off. */}
            {isFN ? FN_EVAC_PUBLIC_ALERT_NOTE : PUBLIC_ALERT_REQUEST_FROM_EVAC_NOTE}
          </p>
          <PublicAlertRequestBlock
            evacuationOrderId={feature.id}
            incidentId={p.incident_id ?? undefined}
            defaultAuthorityName={p.issuing_authority_name}
            defaultAuthorityKind={requestAuthorityKind}
          />
        </section>
      )}

      {isActive && !editing && (
        <section className="flex flex-wrap gap-2">
          {canEscalate && (
            <button
              type="button"
              data-testid="evac-escalate"
              disabled={busy}
              onClick={escalate}
              className="text-xs py-1.5 px-3 rounded bg-violet-700/90 hover:bg-violet-700 border border-violet-500 text-white font-medium disabled:opacity-50 disabled:cursor-wait"
            >
              {busy ? 'Working…' : 'Escalate to ORDER'}
            </button>
          )}
          <button
            type="button"
            data-testid="evac-correct"
            disabled={busy}
            onClick={startCorrection}
            className="text-xs py-1.5 px-3 rounded bg-slate-700/90 hover:bg-slate-700 border border-slate-500 text-white font-medium disabled:opacity-50 disabled:cursor-wait"
          >
            {CORRECT_EVAC_ORDER_LABEL}
          </button>
          <button
            type="button"
            data-testid="evac-rescind"
            disabled={busy}
            onClick={rescind}
            className="text-xs py-1.5 px-3 rounded bg-slate-700/90 hover:bg-slate-700 border border-slate-500 text-white font-medium disabled:opacity-50 disabled:cursor-wait"
          >
            {busy ? 'Working…' : 'Rescind'}
          </button>
        </section>
      )}

      {isFN ? (
        <p className="text-[11px] text-slate-400 leading-relaxed border-t border-slate-800 pt-3">
          Recorded in this COP: an evacuation request submitted to the PEOC (ISC included) for{' '}
          <span className="text-slate-300">{p.issuing_authority_name}</span>. The evacuation decision is a
          Joint Command Call (Chief and Council with ISC). The COP records the request — it does not
          confer authority. Verify the current status with the community and the PEOC before acting.
        </p>
      ) : (
        <p className="text-[11px] text-slate-400 leading-relaxed border-t border-slate-800 pt-3">
          Recorded in this COP on behalf of <span className="text-slate-300">{p.issuing_authority_name}</span>. The
          COP coordinates this order — it does not itself confer the legal authority to evacuate. Verify
          the current order with the issuing authority before acting.
        </p>
      )}
    </div>
  );
}
